/**
 * buildPlanFromAeroway — turn real airport vector geometry (OSM `aeroway`
 * features, lng/lat) into a routable `TaxiPlan` that sits on the actual runway /
 * taxiway pavement the basemap draws, instead of the synthesized field graph.
 *
 * Pipeline:
 *  1. Convert every runway/taxiway vertex lng/lat → scene feet via the same
 *     `CoordinateService` the areas/zones use, so the network aligns with them.
 *  2. **Stitch** shared vertices by quantizing to a small scene-feet grid — OSM
 *     ways share coordinate-identical nodes at junctions, so this reproduces the
 *     real connected network (verified: KAGS → 1 component). Works for any
 *     GeoJSON source, not just OSM.
 *  3. Assign roles: holding positions → `hold_short`, runway endpoints →
 *     `runway_exit` (thresholds), the rest → `taxi`. Edges follow each way
 *     (`taxiway` / `runway`).
 *  4. Wire the apron in via **marked exit waypoints** (`exit_to_taxiway` +
 *     `flags.towerHandoff`) — the ramp ↔ tower-controlled movement-area handoff.
 *     Each authored exit connects to the nearest taxiway node (`exit` edge).
 *  5. Wrap each placeable zone in an `apron_lead` **perimeter loop** (offset by a
 *     margin). It's orientation-independent, so routing snaps a parked spot to the
 *     nearest loop node and taxis around to the exit — no diagonal across the apron.
 *
 * Everything lands in one regenerable group so the editor can re-run it as a unit
 * and the result stays hand-tunable. A* routing (`routeGround`) consumes it
 * unchanged.
 */
import { CoordinateService } from '@/services/3d/CoordinateService'
import type { ZoneGeometry } from '../engine/types'
import type { FieldGraph } from './fieldGraph'
import type { SeedExit } from './seedTaxiPlan'
import {
  alwaysActive,
  makeId,
  type EdgeRole,
  type NodeRole,
  type TaxiEdge,
  type TaxiGroup,
  type TaxiNode,
  type TaxiPlan,
} from './taxiGraph'
import { bearingDeg } from '../motion/easing'
import { synthesizeImportedRunways } from './runwayDesignator'
import { mergeApronMidlines } from './apronMerge'

// ── GeoJSON-ish input shape (a subset; extra props are ignored) ──────────────
export interface AerowayFeature {
  type: 'Feature'
  properties: {
    aeroway: 'runway' | 'taxiway' | 'apron' | 'holding_position' | (string & {})
    osm_id?: number
    ref?: string
    width_m?: number
    length_m?: number
    surface?: string
    hp_type?: string | null
  }
  geometry:
    | { type: 'LineString'; coordinates: number[][] }
    | { type: 'Point'; coordinates: number[] }
    | { type: 'Polygon'; coordinates: number[][][] }
}

export interface AerowayFeatureCollection {
  type: 'FeatureCollection'
  properties?: Record<string, unknown>
  features: AerowayFeature[]
}

export interface BuildPlanFromAerowayOptions {
  /** Authored exit anchors (scene feet) → marked `exit_to_taxiway` waypoints. */
  exits?: SeedExit[]
  /** Placeable zones → an `apron_lead` perimeter loop around each area (orientation-independent). */
  zones?: ZoneGeometry[]
  /** Plan name. */
  name?: string
  /** Vertex-merge grid in scene feet (default 2 ft — OSM junctions are identical). */
  snapFt?: number
  /** Apron perimeter offset outside each zone footprint, scene feet (default 25). */
  perimeterMarginFt?: number
  /** Sampling spacing along apron perimeter sides, scene feet (default 70). */
  perimeterSpacingFt?: number
  /** One apron→movement-area access link per this much perimeter, scene feet (default 800). */
  linkSpacingFt?: number
  /** Apron→taxiway connection policy used when a zone doesn't override it (default 'auto'). */
  defaultConnectorPolicy?: 'auto' | 'single' | 'pair'
  /**
   * Merge facing perimeter lanes of *different* areas whose nodes fall within this
   * distance into one shared midline lane, scene feet (default 80). Cross-area only,
   * so a zone never collapses its own loop.
   */
  apronMergeFt?: number
}

type Pt = [number, number]
const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1])

/**
 * Project point `p` onto segment `a→b`, clamped to the segment. Returns the
 * foot of the perpendicular `q`, its parameter `t∈[0,1]`, and the distance
 * `p→q` — the basis for the shortest apron→taxiway connector.
 */
function projectOnSeg(p: Pt, a: Pt, b: Pt): { q: Pt; t: number; d: number } {
  const abx = b[0] - a[0]
  const abz = b[1] - a[1]
  const len2 = abx * abx + abz * abz
  let t = len2 > 0 ? ((p[0] - a[0]) * abx + (p[1] - a[1]) * abz) / len2 : 0
  t = Math.max(0, Math.min(1, t))
  const q: Pt = [a[0] + abx * t, a[1] + abz * t]
  return { q, t, d: dist(p, q) }
}

function mkNode(groupId: string, pos: Pt, role: NodeRole, extra?: Partial<TaxiNode>): TaxiNode {
  return {
    id: makeId('n'),
    groupId,
    pos: [pos[0], pos[1]],
    role,
    headingDeg: extra?.headingDeg ?? null,
    throughputPerSlot: extra?.throughputPerSlot ?? null,
    active: alwaysActive(),
    flags: extra?.flags ?? {},
  }
}

function mkEdge(groupId: string, a: string, b: string, role: EdgeRole): TaxiEdge {
  return {
    id: makeId('e'),
    groupId,
    a,
    b,
    role,
    directed: false,
    weightMult: 1,
    active: alwaysActive(),
    closures: [],
    flags: {},
  }
}

/**
 * Wrap each placeable zone in an orientation-independent `apron` perimeter loop
 * (offset by a margin, sampled along each side at `perimeterSpacingFt`). Returns
 * the per-zone loops plus the flat node + loop-edge lists the midline merge needs.
 */
function buildZoneLoops(
  zones: ZoneGeometry[],
  groupId: string,
  perimeterMarginFt: number,
  perimeterSpacingFt: number
): {
  zoneLoops: Array<{ z: ZoneGeometry; loop: TaxiNode[] }>
  apronNodes: TaxiNode[]
  apronEdgePairs: Array<[TaxiNode, TaxiNode]>
} {
  const zoneLoops: Array<{ z: ZoneGeometry; loop: TaxiNode[] }> = []
  const apronNodes: TaxiNode[] = []
  const apronEdgePairs: Array<[TaxiNode, TaxiNode]> = []
  for (const z of zones) {
    const rad = (z.rotationDeg * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    // local (lx = width/+X, lz = depth/+Z) → scene feet, matching buildSlotGrid.
    const L = (lx: number, lz: number): Pt => [
      z.center[0] + lx * cos + lz * sin,
      z.center[1] - lx * sin + lz * cos,
    ]
    const hw = z.width / 2 + perimeterMarginFt
    const hd = z.depth / 2 + perimeterMarginFt
    const corners: Pt[] = [
      [-hw, -hd],
      [hw, -hd],
      [hw, hd],
      [-hw, hd],
    ]
    // sample each side (incl. its start corner; the next side starts the next
    // corner, so the closed loop never duplicates a corner).
    const loop: TaxiNode[] = []
    for (let s = 0; s < 4; s++) {
      const [ax, az] = corners[s]
      const [bx, bz] = corners[(s + 1) % 4]
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / perimeterSpacingFt))
      for (let i = 0; i < steps; i++) {
        const t = i / steps
        loop.push(
          mkNode(groupId, L(ax + (bx - ax) * t, az + (bz - az) * t), 'apron', {
            flags: { apron: true, zone: z.areaId },
          })
        )
      }
    }
    for (const n of loop) apronNodes.push(n)
    for (let i = 0; i < loop.length; i++) apronEdgePairs.push([loop[i], loop[(i + 1) % loop.length]])
    zoneLoops.push({ z, loop })
  }
  return { zoneLoops, apronNodes, apronEdgePairs }
}

/**
 * Build a routable `TaxiPlan` from aeroway geometry. `id`/`locationId` are
 * placeholders until persisted (Save assigns the DB id), mirroring `buildSeedPlan`.
 */
export function buildPlanFromAeroway(
  locationId: string,
  geo: AerowayFeatureCollection,
  mapCenter: { lat: number; lng: number },
  opts: BuildPlanFromAerowayOptions = {}
): TaxiPlan {
  const snap = opts.snapFt ?? 2
  const group: TaxiGroup = {
    id: makeId('g'),
    name: 'Imported (real geometry)',
    role: 'permanent',
    color: '#34d399',
    active: alwaysActive(),
    flags: { imported: true },
  }

  const toScene = (lng: number, lat: number): Pt => {
    const [x, , z] = CoordinateService.lngLatToScenePosition([lng, lat], [mapCenter.lng, mapCenter.lat])
    return [x, z]
  }

  // ── 1+2. stitch vertices into shared nodes ─────────────────────────────────
  const nodeByKey = new Map<string, TaxiNode>()
  const keyOf = (p: Pt) => `${Math.round(p[0] / snap)}|${Math.round(p[1] / snap)}`
  const getNode = (p: Pt): TaxiNode => {
    const k = keyOf(p)
    let n = nodeByKey.get(k)
    if (!n) {
      n = mkNode(group.id, p, 'taxi')
      nodeByKey.set(k, n)
    }
    return n
  }

  const edges: TaxiEdge[] = []
  const edgeSeen = new Set<string>()
  const addEdge = (a: TaxiNode, b: TaxiNode, role: EdgeRole) => {
    if (a.id === b.id) return
    const ek = a.id < b.id ? `${a.id}:${b.id}` : `${b.id}:${a.id}`
    if (edgeSeen.has(ek)) return
    edgeSeen.add(ek)
    edges.push(mkEdge(group.id, a.id, b.id, role))
  }

  const taxiwayNodeIds = new Set<string>()
  const runwayEndpoints: TaxiNode[] = []
  /** Raw runway features (ref/width/surface + stitched end nodes) → TaxiRunway later. */
  const runwayFeatures: Array<{
    ref?: string
    widthM?: number
    surface?: string
    endA: TaxiNode
    endB: TaxiNode
  }> = []

  for (const f of geo.features) {
    const a = f.properties.aeroway
    if ((a !== 'taxiway' && a !== 'runway') || f.geometry.type !== 'LineString') continue
    const role: EdgeRole = a === 'runway' ? 'runway' : 'taxiway'
    const chain = f.geometry.coordinates.map(([lng, lat]) => getNode(toScene(lng, lat)))
    for (let i = 0; i < chain.length - 1; i++) addEdge(chain[i], chain[i + 1], role)
    if (a === 'taxiway') for (const n of chain) taxiwayNodeIds.add(n.id)
    if (a === 'runway' && chain.length) {
      runwayEndpoints.push(chain[0], chain[chain.length - 1])
      runwayFeatures.push({
        ref: f.properties.ref,
        widthM: f.properties.width_m,
        surface: f.properties.surface,
        endA: chain[0],
        endB: chain[chain.length - 1],
      })
    }
  }

  // ── 3. roles: holding positions → hold_short; runway thresholds → runway_exit ─
  const allNodes = [...nodeByKey.values()]
  for (const f of geo.features) {
    if (f.properties.aeroway !== 'holding_position' || f.geometry.type !== 'Point') continue
    const p = toScene(f.geometry.coordinates[0], f.geometry.coordinates[1])
    let best: TaxiNode | null = null
    let bestD = Infinity
    for (const n of allNodes) {
      const d = dist(n.pos, p)
      if (d < bestD) {
        bestD = d
        best = n
      }
    }
    if (best && bestD <= Math.max(snap * 4, 25)) {
      best.role = 'hold_short'
      // Face the runway: heading toward the nearest runway endpoint (so the node's
      // flow direction reads "about to enter the runway").
      let near: TaxiNode | null = null
      let nearD = Infinity
      for (const r of runwayEndpoints) {
        const d = dist(best.pos, r.pos)
        if (d < nearD) {
          nearD = d
          near = r
        }
      }
      if (near) best.headingDeg = Math.round(bearingDeg(best.pos, near.pos))
    }
  }
  for (const n of runwayEndpoints) {
    if (n.role === 'taxi') n.role = 'runway_exit' // takeoff/landing threshold
  }

  // ── 4. apron → marked exit waypoints (the tower-zone handoff) ───────────────
  const nodes = [...nodeByKey.values()]
  const taxiwayNodes = nodes.filter((n) => taxiwayNodeIds.has(n.id))
  const nearestOf = (cands: TaxiNode[], p: Pt): TaxiNode | null => {
    let best: TaxiNode | null = null
    let bestD = Infinity
    for (const n of cands) {
      const d = dist(n.pos, p)
      if (d < bestD) {
        bestD = d
        best = n
      }
    }
    return best
  }
  const nearestTaxiway = (p: Pt): TaxiNode | null => nearestOf(taxiwayNodes, p)

  const exitNodes: TaxiNode[] = []
  for (const ex of opts.exits ?? []) {
    const en = mkNode(group.id, ex.pos, 'exit_to_taxiway', {
      headingDeg: ex.headingDeg ?? null,
      throughputPerSlot: ex.throughputPerSlot ?? null,
      flags: { name: ex.name, towerHandoff: true },
    })
    nodes.push(en)
    exitNodes.push(en)
    const near = nearestTaxiway(ex.pos)
    if (near) addEdge(en, near, 'exit')
  }

  // ── 5. apron perimeters + connections. Each placeable zone gets a margin
  //       `apron_lead` perimeter loop (orientation-independent, so routeGround
  //       snaps a parked spot to the nearest loop node and taxis around the loop
  //       instead of cutting a diagonal). Then: (C) facing lanes of *nearby*
  //       areas weld onto one shared midline lane, and (A/B) each perimeter joins
  //       the movement area via the shortest *perpendicular* connector onto the
  //       nearest taxiway edge (or its exit-waypoint tower handoff), per a
  //       per-area `connectorPolicy`.
  const perimeterMarginFt = opts.perimeterMarginFt ?? 25
  const perimeterSpacingFt = opts.perimeterSpacingFt ?? 70
  const linkSpacingFt = opts.linkSpacingFt ?? 800
  const apronMergeFt = opts.apronMergeFt ?? 80
  const defaultConnectorPolicy = opts.defaultConnectorPolicy ?? 'auto'

  // Drop an edge by id (used to split a taxiway edge around a connector node).
  const removeEdge = (id: string) => {
    const idx = edges.findIndex((e) => e.id === id)
    if (idx === -1) return
    const e = edges[idx]
    edges.splice(idx, 1)
    edgeSeen.delete(e.a < e.b ? `${e.a}:${e.b}` : `${e.b}:${e.a}`)
  }

  // (1) Build each zone's perimeter loop (orientation-independent) — see buildZoneLoops.
  const { zoneLoops, apronNodes, apronEdgePairs } = buildZoneLoops(
    opts.zones ?? [],
    group.id,
    perimeterMarginFt,
    perimeterSpacingFt
  )

  // (C) Shared midline merge — collapse facing perimeter lanes of *different*
  //     nearby zones onto one shared midline lane (see apronMerge.ts).
  const { repById, emitted } = mergeApronMidlines(apronNodes, apronMergeFt, (pos, zones) =>
    mkNode(group.id, pos, 'apron', { flags: { apron: true, shared: true, zones } })
  )
  for (const n of emitted) nodes.push(n)
  // Emit (remapped, deduped) perimeter loop edges; addEdge drops self-loops + dupes.
  for (const [a, b] of apronEdgePairs) addEdge(repById.get(a.id)!, repById.get(b.id)!, 'apron_lead')

  // (A/B) Connect each zone's perimeter to the movement area. Snapshot taxiway
  //       segments first; a connector projects perpendicular onto the nearest
  //       taxiway edge and splits it, unless a marked exit waypoint is closer
  //       (the tower handoff). Selection per zone follows `connectorPolicy`.
  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const taxiwaySegById = new Map<string, { id: string; a: TaxiNode; b: TaxiNode }>()
  for (const e of edges) {
    if (e.role !== 'taxiway') continue
    const a = nodeById.get(e.a)
    const b = nodeById.get(e.b)
    if (a && b) taxiwaySegById.set(e.id, { id: e.id, a, b })
  }
  const taxiwaySegs = [...taxiwaySegById.values()]
  const nearestTaxiwaySeg = (p: Pt): { segId: string; t: number; q: Pt; d: number } | null => {
    let best: { segId: string; t: number; q: Pt; d: number } | null = null
    for (const seg of taxiwaySegs) {
      const pr = projectOnSeg(p, seg.a.pos, seg.b.pos)
      if (!best || pr.d < best.d) best = { segId: seg.id, t: pr.t, q: pr.q, d: pr.d }
    }
    return best
  }

  const facingBandFt = perimeterSpacingFt // a node "faces" a taxiway if ~as close as the closest
  const taxiwayConnectors: Array<{ pn: TaxiNode; segId: string; t: number; q: Pt }> = []
  for (const { z, loop } of zoneLoops) {
    const policy = z.connectorPolicy ?? defaultConnectorPolicy
    // merged, deduped perimeter nodes, in loop order
    const perim: TaxiNode[] = []
    const seenP = new Set<string>()
    for (const n of loop) {
      const r = repById.get(n.id)!
      if (!seenP.has(r.id)) {
        seenP.add(r.id)
        perim.push(r)
      }
    }
    if (perim.length === 0) continue

    // nearest exit waypoint to any perimeter node (the tower handoff)
    let bestExit: { pn: TaxiNode; exit: TaxiNode; d: number } | null = null
    for (const pn of perim) {
      for (const ex of exitNodes) {
        const d = dist(pn.pos, ex.pos)
        if (!bestExit || d < bestExit.d) bestExit = { pn, exit: ex, d }
      }
    }
    // nearest taxiway segment per perimeter node
    const segOf = new Map<string, { segId: string; t: number; q: Pt; d: number }>()
    let dtMin = Infinity
    for (const pn of perim) {
      const ns = nearestTaxiwaySeg(pn.pos)
      if (ns) {
        segOf.set(pn.id, ns)
        if (ns.d < dtMin) dtMin = ns.d
      }
    }
    const de = bestExit ? bestExit.d : Infinity

    // If an exit waypoint is the closest access, hand off there (single link) —
    // preserves the west-cluster ramp → orange-dot → taxiway tower handoff.
    if (de <= dtMin) {
      if (bestExit) addEdge(bestExit.pn, bestExit.exit, 'apron_lead')
      continue
    }

    // Taxiway-adjacent: connector nodes on the facing side(s) (≈ as close as the
    // closest). Project each perpendicular onto its nearest taxiway edge.
    const facing = perim.filter((pn) => {
      const s = segOf.get(pn.id)
      return s !== undefined && s.d <= dtMin + facingBandFt
    })
    if (facing.length === 0) continue
    facing.sort((a, b) => segOf.get(a.id)!.d - segOf.get(b.id)!.d)
    const pushConn = (pn: TaxiNode) => {
      const s = segOf.get(pn.id)!
      taxiwayConnectors.push({ pn, segId: s.segId, t: s.t, q: s.q })
    }

    if (policy === 'single') {
      pushConn(facing[0])
    } else if (policy === 'pair') {
      pushConn(facing[0])
      // second connector at the opposite end (farthest facing node from the first)
      let second: TaxiNode | null = null
      let spread = 0
      for (const pn of facing) {
        const sp = dist(pn.pos, facing[0].pos)
        if (sp > spread) {
          spread = sp
          second = pn
        }
      }
      if (second && second.id !== facing[0].id && spread >= linkSpacingFt * 0.5) pushConn(second)
    } else {
      // auto: spaced connectors, one per ~linkSpacingFt of perimeter, capped.
      const perimeterLen = 2 * (z.width + z.depth + 4 * perimeterMarginFt)
      const maxLinks = Math.max(1, Math.round(perimeterLen / linkSpacingFt))
      const accepted: TaxiNode[] = []
      for (const pn of facing) {
        if (accepted.length >= maxLinks) break
        if (accepted.some((a) => dist(a.pos, pn.pos) < linkSpacingFt)) continue
        accepted.push(pn)
        pushConn(pn)
      }
    }
  }

  // Split taxiway edges at the chosen connector feet, then join the perimeters
  // in. Group by segment so an edge with several connectors splits once into a
  // chain (a→cut₁→…→b), merging cuts within `snap` and snapping near-ends.
  const bySeg = new Map<string, Array<{ pn: TaxiNode; t: number; q: Pt }>>()
  for (const c of taxiwayConnectors) {
    const arr = bySeg.get(c.segId)
    if (arr) arr.push({ pn: c.pn, t: c.t, q: c.q })
    else bySeg.set(c.segId, [{ pn: c.pn, t: c.t, q: c.q }])
  }
  for (const [segId, cuts] of bySeg) {
    const seg = taxiwaySegById.get(segId)
    if (!seg) continue
    const segLen = dist(seg.a.pos, seg.b.pos) || 1
    cuts.sort((a, b) => a.t - b.t)
    const interior: Array<{ t: number; node: TaxiNode }> = []
    const targetFor: Array<{ pn: TaxiNode; node: TaxiNode }> = []
    for (const c of cuts) {
      if (c.t * segLen <= snap) {
        targetFor.push({ pn: c.pn, node: seg.a })
        continue
      }
      if ((1 - c.t) * segLen <= snap) {
        targetFor.push({ pn: c.pn, node: seg.b })
        continue
      }
      const last = interior[interior.length - 1]
      if (last && Math.abs(last.t - c.t) * segLen <= snap) {
        targetFor.push({ pn: c.pn, node: last.node })
        continue
      }
      const node = mkNode(group.id, c.q, 'taxi')
      nodes.push(node)
      interior.push({ t: c.t, node })
      targetFor.push({ pn: c.pn, node })
    }
    if (interior.length) {
      removeEdge(segId)
      const chain = [seg.a, ...interior.map((x) => x.node), seg.b]
      for (let i = 0; i < chain.length - 1; i++) addEdge(chain[i], chain[i + 1], 'taxiway')
    }
    for (const { pn, node } of targetFor) addEdge(pn, node, 'apron_lead')
  }

  // ── 6. authorable runways from the runway features ─────────────────────────
  const runways = synthesizeImportedRunways(runwayFeatures, group.id)

  return {
    id: makeId('plan'),
    locationId,
    name: opts.name ?? 'Real-geometry baseline',
    description:
      'Auto-generated from real airport (aeroway) geometry. Exit waypoints mark the ramp ↔ ' +
      'movement-area handoff. Fine-tune nodes/edges as needed.',
    isBaseline: true,
    active: alwaysActive(),
    groups: [group],
    nodes,
    edges,
    runways,
    flags: { imported: true, source: 'aeroway' },
  }
}

/**
 * Align a synthetic `FieldGraph`'s runway fields to the real primary (longest)
 * runway from aeroway geometry. The motion sim draws airborne legs (final/climb)
 * between these thresholds and routes ground legs *to* them, so without this the
 * aircraft taxi toward a fabricated runway position. Spine/gates (tug staging +
 * routeTaxi fallback) are kept from `base` — they stay apron-relative.
 */
export function fieldGraphFromAeroway(
  base: FieldGraph,
  geo: AerowayFeatureCollection,
  mapCenter: { lat: number; lng: number },
  finalRunFt = 1400
): FieldGraph {
  const toScene = (lng: number, lat: number): Pt => {
    const [x, , z] = CoordinateService.lngLatToScenePosition([lng, lat], [mapCenter.lng, mapCenter.lat])
    return [x, z]
  }
  let best: Pt[] | null = null
  let bestLen = 0
  for (const f of geo.features) {
    if (f.properties.aeroway !== 'runway' || f.geometry.type !== 'LineString') continue
    const pts = f.geometry.coordinates.map((c) => toScene(c[0], c[1]))
    let len = 0
    for (let i = 1; i < pts.length; i++) len += dist(pts[i - 1], pts[i])
    if (len > bestLen) {
      bestLen = len
      best = pts
    }
  }
  if (!best || best.length < 2) return base
  const A = best[0]
  const B = best[best.length - 1]
  const L = dist(A, B) || 1
  const fwd: Pt = [(B[0] - A[0]) / L, (B[1] - A[1]) / L]
  // Scene heading: 0 = north (−Z), 90 = east (+X) — matches the motion sim's convention.
  const bearing = ((Math.atan2(fwd[0], -fwd[1]) * 180) / Math.PI + 360) % 360
  return {
    ...base,
    runwayBearingDeg: bearing,
    approachBearingDeg: bearing,
    departureBearingDeg: bearing,
    thresholdApproach: A,
    thresholdDeparture: B,
    finalEntry: [A[0] - fwd[0] * finalRunFt, A[1] - fwd[1] * finalRunFt],
    // No on-runway roll for the bare aeroway fallback (collapse to thresholds);
    // real roll geometry comes from `fieldGraphFromPlan` once a runway is authored.
    touchdownPoint: A,
    rolloutEnd: A,
    rotationPoint: B,
    climbExit: [B[0] + fwd[0] * finalRunFt, B[1] + fwd[1] * finalRunFt],
    runwayAccess: [A, B],
  }
}
